import React from 'react';
import { Link } from 'react-router-dom';
import Phone from 'lucide-react/dist/esm/icons/phone';
import Mail from 'lucide-react/dist/esm/icons/mail';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer>
      {/* Accreditation Section */}
      <div className="bg-white py-12">
        <div className="container-custom">
          <div className="text-center">
            <h4 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-6 text-gray-900">
              Accredited & Trusted By
            </h4>
            <div className="flex flex-wrap justify-center items-center gap-12">
              <img
                src="https://media.phst.co.za/file/omc/saica-logo.svg"
                alt="SAICA"
                className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://media.phst.co.za/file/omc/lgseta-logo2.png"
                alt="LGSETA"
                className="h-14 w-auto opacity-70 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://media.phst.co.za/file/omc/RA-Logo.jpg"
                alt="Registered Auditor"
                className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://media.phst.co.za/file/omc/AGSA-Logo.png"
                alt="Auditor-General South Africa"
                className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-[#00338D] text-white">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <img
                src="https://media.phst.co.za/file/omc/omc-logo-white.png"
                alt="OMC Chartered Accountants"
                className="h-12 w-auto mb-4"
              />
              <p className="text-gray-300 mb-4">
                Setting the standard in accounting, auditing, and advisory excellence across South Africa.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none">
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/careers" className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none">
                    Careers
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Our Services</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link 
                    to="/services#external-audit" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Audit & Assurance
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#internal-audit" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Internal Audit
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#forensic" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Forensic Investigations
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#it-risk" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    IT Risk & Advisory
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#consulting" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Consulting Services
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#public-sector" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Public Sector Solutions
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services#venture-capital" 
                    className="text-gray-300 hover:text-white transition-colors bg-transparent focus:outline-none"
                  >
                    Venture Capital
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-secondary mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-gray-300 text-sm">
                    <p>3 North Close</p>
                    <p>Sunninghill Estate, 2191</p>
                    <p>Johannesburg, South Africa</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-secondary mr-3" />
                  <a href="tel:+27100011234" className="text-gray-300 hover:text-white transition-colors text-sm">
                    +27 10 001 1234
                  </a>
                </div>
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-secondary mr-3" />
                  <a href="mailto:admin@omcca.co.za" className="text-gray-300 hover:text-white transition-colors text-sm">
                    admin@omcca.co.za
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="container-custom py-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400 gap-4">
            {/* Left side - Copyright and Legal Links */}
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-0">
              <p className="text-center md:text-left">
                &copy; {currentYear} OMC Chartered Accountants (Pty) Ltd. All rights reserved.
              </p>
              <span className="hidden md:inline mx-2">|</span>
              <div className="flex items-center gap-2">
                <Link 
                  to="/terms-of-use" 
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Terms of Use
                </Link>
                <span>|</span>
                <Link 
                  to="/privacy-policy" 
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
                <span className="text-transparent">|</span>
  <Link 
    to="/admin-contact" 
    className="text-transparent hover:text-transparent transition-colors cursor-pointer"
  >
    Admin
  </Link>
              </div>
            </div>

            {/* Right side - Website Credit */}
            <div className="flex items-center">
              <span className="mr-2">Website by</span>
              <a
                href="https://www.primehost.co.za/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:text-white transition-colors flex items-center"
              >
                Primehost
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;